import styles from '@components/components/CmsComponents/components/CmsVotingWidget/CmsVotingWidget.module.scss';
import api from '@components/api';
import { useAjax, useSessionStorage, useTracking } from '@components/hooks';
import { emailRegex } from '@components/utils';
import { Voting } from '@wienenergiegithub/ui-next';
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormStore } from '@ariakit/react';

const CmsVotingWidget = ({
  answers,
  id,
  isRaffle = false,
  question,
  receiverEmail,
  title,
  votingType,
  ...props
}) => {
  const [submitVote] = useAjax('we_theme_submit_vote');
  const [submitVotingRaffleData] = useAjax(
    'we_theme_submit_voting_raffle_data',
  );
  const topRef = useRef(null);
  const [storage, setStorage] = useSessionStorage(title, null);
  const [step, setStep] = useState(0);
  const track = useTracking();

  const validateEmail = mail => emailRegex.test(mail);

  const handleValueSubmit = async (type, value) => {
    const requestData = {
      voting_id: id,
      voting_title: title,
      voting_type: type,
      voting_value: value,
      answers: answers || [],
    };
    try {
      const { data: response } = await submitVote(requestData);

      if (!response.success) {
        throw new Error(response.data[0].message);
      }

      if (votingType !== 'list') {
        track({
          event: 'stimmungsbarometer',
          options: {
            stimmungsVoting: value,
            stimmungsFrage: question,
          },
        });
      }

      form.setValue('answer', value);
      setStorage(response.data);
    } catch (e) {
      setStorage(null);
    } finally {
      if (isRaffle) {
        setStep(prev => prev + 1);
      }
    }
  };

  const [values, setValues] = useState({
    title,
    answer: null,
    anrede: '',
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    accepted: false,
  });

  const form = useFormStore({ values, setValues });

  form.useValidate(() => {
    if (!values.vorname) {
      form.setError('vorname', 'Vorname muss ausgefüllt sein.');
    }

    if (!values.nachname) {
      form.setError('nachname', 'Nachname muss ausgefüllt sein.');
    }

    if (!validateEmail(values.email)) {
      form.setError('email', 'Bitte überprüfen Sie Ihre Eingabe.');
    }

    if (!values.email) {
      form.setError('email', 'Email muss ausgefüllt sein.');
    }

    if (!values.telefon) {
      form.setError('telefon', 'Telefonnummer muss ausgefüllt sein.');
    }

    if (!values.accepted) {
      form.setError(
        'accepted',
        'Bitte akzeptieren Sie die Teilnahmebedingungen.',
      );
    }
  });

  form.useSubmit(async () => {
    try {
      if (topRef.current) {
        topRef.current.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }

      const postValues = {
        title,
        answer: values.answer,
        receiverEmail,
        email: values.email,
        salutation: values.anrede,
        accepted: values.accepted,
        firstname: values.vorname,
        lastname: values.nachname,
        phone: values.telefon,
      };
      const formData = new FormData();

      Object.entries(postValues).forEach(([key, value]) => {
        if (typeof value === 'string') {
          formData.append(key, value);
        } else {
          formData.append(key, JSON.stringify(value));
        }
      });

      const { data: response } = await submitVotingRaffleData(formData);

      if (!response?.success) {
        throw new Error(response.data[0].message);
      }

      if (votingType !== 'list') {
        track({
          event: 'gewinnspiel teilgenommen',
          title: 'stimmungsbarometer',
        });
      }

      setStep(prev => prev + 1);
    } catch (error) {
      /* empty */
    }
  });

  return (
    <div className={styles.container} ref={topRef}>
      <Voting
        answers={answers || []}
        id={String(id)}
        onSubmitValue={handleValueSubmit}
        question={question}
        results={storage}
        step={step}
        store={form}
        votingType={votingType}
        {...props}
      />
    </div>
  );
};

CmsVotingWidget.propTypes = {
  answers: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.number.isRequired,
  isRaffle: PropTypes.bool,
  question: PropTypes.string.isRequired,
  receiverEmail: PropTypes.string,
  title: PropTypes.string.isRequired,
  votingType: PropTypes.oneOf(['slider', 'toggle', 'list']).isRequired,
};

export default CmsVotingWidget;
